import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { Export, ExportWithUrl, Project } from "../lib/types.js";
import { usePolling } from "../lib/usePolling.js";

const btnPrimary =
  "shrink-0 rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const selectCls =
  "rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

function when(iso?: string): string {
  return iso ? new Date(iso).toLocaleString() : "-";
}

function size(bytes?: number): string {
  if (bytes === undefined) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Per-project export zips (Task 4.3). Create fires the async builder; the row
 *  polls itself from `building` to `ready`, at which point Download fetches a
 *  fresh 1 h presigned URL and navigates to it. */
export function Exports() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<string>("");
  const [rows, setRows] = useState<Export[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api<{ items: Project[] }>("/api/projects");
        setProjects(res.items);
        if (res.items.length > 0) setProjectId((cur) => cur || res.items[0].projectId);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : "Could not load projects.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadExports = useCallback(async (pid: string) => {
    if (!pid) return;
    try {
      const res = await api<{ items: Export[] }>(`/api/projects/${pid}/exports`);
      setRows(res.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load exports.");
    }
  }, []);

  useEffect(() => {
    setRows([]);
    if (projectId) void loadExports(projectId);
  }, [projectId, loadExports]);

  // Re-fetch while any export is still building, via the shared polling seam.
  const anyBuilding = useMemo(() => rows.some((r) => r.status === "building"), [rows]);
  usePolling(() => loadExports(projectId), { enabled: anyBuilding && Boolean(projectId) });

  async function handleCreate() {
    if (!projectId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const row = await api<Export>(`/api/projects/${projectId}/exports`, { method: "POST", json: {} });
      setRows((prev) => [row, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the export.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload(exportId: string) {
    setError(null);
    try {
      const detail = await api<ExportWithUrl>(`/api/exports/${exportId}`);
      if (detail.url) window.location.href = detail.url;
      else setError("That export is not ready to download yet.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not fetch the download link.");
    }
  }

  if (loading) return <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading...</p>;

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Exports</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        A zip per project with <code>manifest.json</code> and every accepted output.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select
          className={selectCls}
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-label="Project"
        >
          {projects.map((p) => (
            <option key={p.projectId} value={p.projectId}>
              {p.name}
            </option>
          ))}
        </select>
        <button type="button" className={btnPrimary} disabled={!projectId || busy} onClick={() => void handleCreate()}>
          {busy ? "Starting..." : "New export"}
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <ul className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
        {rows.map((r) => (
          <li key={r.exportId} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={
                    r.status === "ready"
                      ? "rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200"
                      : r.status === "failed"
                        ? "rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200"
                        : "rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                  }
                >
                  {r.status}
                </span>
                <span className="truncate text-sm text-gray-500 dark:text-gray-400">
                  {when(r.createdAt)}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {r.status === "ready" && `${r.itemCount ?? 0} items - ${size(r.bytes)}`}
                {r.status === "building" && "Building..."}
                {r.status === "failed" && "Build failed - try again."}
              </div>
            </div>
            <button
              type="button"
              className={btnPrimary}
              disabled={r.status !== "ready"}
              onClick={() => void handleDownload(r.exportId)}
            >
              Download
            </button>
          </li>
        ))}
        {rows.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No exports for this project yet.
          </li>
        )}
      </ul>
    </div>
  );
}
